<template>
  <div class="tags-wrapper">
    <div ref="canvasWrapper" :style="bgStyle">
      <div v-if="showTagLayer" class="tag-layer">
        <div v-for="tag in tags">
          <component
            :is="componentMap[tag.type]"
            v-bind="localizeTagAsset(tag)"
            :class="{
              active: isActive(tag._id),
              inactive: isInactive(tag._id),
            }"
            @pointerdown.native="handleDragStart(tag, $event)"
            @pointerup.native="handleDragEnd($event)"
            @pointermove.native="handleDrag($event)"
            @dblclick.native="onDoubleClick(tag, $event)"
          />
        </div>
      </div>

      <div v-if="showSlideLayer" class="tag-layer">
        <div v-for="tag in activeSlide.tags">
          <component
            :is="componentMap[tag.type]"
            v-bind="localizeTagAsset(tag)"
            :class="{
              active: isActive(tag._id),
              inactive: isInactive(tag._id),
            }"
            @pointerdown.native="handleDragStart(tag, $event)"
            @pointerup.native="handleDragEnd($event)"
            @pointermove.native="handleDrag($event)"
            @dblclick.native="onDoubleClick(tag, $event)"
          />
        </div>
      </div>

      <reference-tag-slider
        v-if="referenceTagsSlider.length > 0"
        :key="`referenceTagSlider-${page.verticalSlider}-${referenceTagsSlider.length}`"
        :isVertical="page.verticalSlider"
        :scaledAmount="scale"
        :tagRef="referenceTagsSlider"
        @click.native.stop
      />

      <div
        v-if="page"
        class="interactive-slides"
        :key="'slide' + interactiveSlides.length"
      >
        <draggable
          tag="div"
          class="base-items-inner d-flex"
          v-model="interactiveSlides"
          v-bind="dragOptionsSlides"
          :group="{ pull: false }"
        >
          <div
            v-for="slide in interactiveSlides"
            :key="slide._id"
            class="interactive-slides__slide"
            :class="[
              { 'interactive-slides__slide--no-bg': !slide.assetId },
              {
                'interactive-slides__slide--inactive':
                  activeSlide && slide._id !== activeSlide._id,
              },
            ]"
            @click.stop="selectSlide(slide._id)"
            @dblclick="onDoubleClick(slide, $event)"
          >
            <div
              class="interactive-slides__label"
              v-html="slide.label[activeLanguage.code]"
            ></div>
            <img
              v-if="slide.asset"
              :src="`${$store.state.assetsPath}${
                slide.asset.customThumbPath
                  ? slide.asset.customThumbPath
                  : slide.asset.thumbPath
              }`"
              alt=""
            />
          </div>
        </draggable>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import draggable from "vuedraggable";
import InfoBox from "@/components/InfoBox";
import ReferenceTagSlider from "@/components/ReferenceTagSlider";
import TagAction from "@/components/TagAction";
import TagBasic from "@/components/TagBasic";
import TagBasicIcon from "@/components/TagBasicIcon";
import TagBox from "@/components/TagBox";
import TagFeatureText from "@/components/TagFeatureText";
import TagReveal from "@/components/TagReveal";
import TagRich from "@/components/TagRich";
import TagReference from "@/components/TagReference";
import { FETCH_PAGE } from "@/store/actions.type";

export default {
  props: [
    "value",
    "active-element",
    "active-slide",
    "selected-elements",
    "zoom-level",
  ],

  components: {
    draggable,
    InfoBox,
    ReferenceTagSlider,
    TagAction,
    TagBasic,
    TagBasicIcon,
    TagBox,
    TagFeatureText,
    TagReveal,
    TagRich,
    TagReference,
  },

  data() {
    return {
      componentMap: {
        action: "TagAction",
        basic: "TagBasic",
        basicIcon: "TagBasicIcon",
        box: "TagBox",
        reference: "TagReference",
        reveal: "TagReveal",
        rich: "TagRich",
      },

      dragOptionsSlides: {
        animation: 120,
        group: "slides",
        disabled: false,
        ghostClass: "ghostly",
      },

      scale: 1,

      isTagDraggable: false,
      isTagDragging: false,

      clickOffsetX: null,
      clickOffsetY: null,
    };
  },

  computed: {
    ...mapGetters(["activeLanguage", "page"]),

    tags: {
      get() {
        return this.value;
      },
      set(tags) {
        this.$emit("input", tags);
      },
    },

    localizedTags() {
      return (this.tags || []).map(tag => this.localizeTagAsset(tag));
    },

    interactiveSlides: {
      get: function () {
        return this.page.interactiveSlides;
      },

      set: function (newValue) {
        this.page.interactiveSlides = newValue;
      },
    },

    showTagLayer() {
      if (this.activeSlide) return false;
      if (!this.activeElement) return true;

      const types = ["slide", "slidetag"];
      if (types.includes(this.activeElement.elementType)) return false;

      return true;
    },

    showSlideLayer() {
      if (!this.activeSlide) return false;

      return true;
    },

    bgStyle() {
      if (this.scale) {
        return {
          ["font-size"]: 20 * this.scale + "px",
          ["height"]: 1960 * this.scale + "px",
        };
      }
      return {};
    },

    referenceTagsSlider() {
      if (this.localizedTags) {
        let refTags = this.localizedTags.filter((tag) => tag.type == "reference");

        refTags = _.orderBy(refTags, ["pos.x"], ["asc"]);

        if (this.dir === "rtl") {
          let startingIndex = refTags.length;

          refTags.forEach((tag, index) => {
            if (!tag.hideTagReference) {
              tag.referenceIndex = startingIndex--;
            }
          });
          return refTags;
        } else {
          refTags.forEach((tag, index) => {
            if (!tag.hideTagReference) {
              tag.referenceIndex = index + 1;
            }
          });
          return refTags;
        }
      } else {
        return [];
      }
    },
  },

  watch: {
    zoomLevel() {
      this.getScaledAmount();
    },
  },

  mounted() {
    const id = this.$route.params.id;
    const current = this.$store.getters.page;

    // avoid refetching and overwriting in-progress edits
    if (!current || current._id !== id) {
      this.$store.dispatch(FETCH_PAGE, id);
    }

    window.addEventListener("resize", this.getScaledAmount);
    this.$nextTick(() => {
      this.getScaledAmount();
    });
  },

  methods: {
    getLocalizedTagAssetId(tag) {
      const langCode =
        this.activeLanguage && this.activeLanguage.code
          ? this.activeLanguage.code
          : "en";

      if (
        tag.assetId &&
        !Array.isArray(tag.assetId) &&
        typeof tag.assetId === "object"
      ) {
        return tag.assetId[langCode] || tag.assetId.en;
      }

      return tag.assetId;
    },

    localizeTagAsset(tag) {
      const assetId = this.getLocalizedTagAssetId(tag);
      const asset = this.$store.state.assetLibrary.assets.find(item => {
        return item._id === assetId;
      });
      const canUsePopulatedAsset =
        !tag.assetId ||
        typeof tag.assetId !== "object" ||
        assetId === tag.assetId.en;

      return {
        ...tag,
        assetId,
        asset: asset || (canUsePopulatedAsset ? tag.asset : null) || null,
      };
    },

    getTagBoundingBox(tagEl, type) {
      // Some tags are structured differently and won't work properly
      // Basic icon, Reveal, Box
      const canvasHeight = 1960;
      const canvasWidth = 3190;
      const canvasLayer = document
        .querySelector(".draggable-container")
        .getBoundingClientRect();

      const elementClasses = [
        ".tag-dot",
        ".tag-rich",
        ".tag-rich__asset-col",
        ".tag-basic",
        ".tag-action",
        ".tag-ref",
      ];

      const selectorString = elementClasses.join(", ");
      const children = tagEl.querySelectorAll(selectorString);
      const elements = [tagEl, ...Array.from(children)];

      const tagAnchorRect = tagEl.getBoundingClientRect();

      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;

      // Loop through all elements and update the min/max coordinates
      elements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        if (rect.width > 0 || rect.height > 0) {
          minX = Math.min(minX, rect.left);
          minY = Math.min(minY, rect.top);
          maxX = Math.max(maxX, rect.right);
          maxY = Math.max(maxY, rect.bottom);
        }
      });

      // tag distance from the canvas edge in pixels
      const leftDistance = minX - canvasLayer.left;
      const rightDistance = canvasLayer.right - maxX;
      const topDistance = minY - canvasLayer.top;
      const bottomDistance = canvasLayer.bottom - maxY;

      // anchor position relative to tag element
      const anchorLeft = tagAnchorRect.left - canvasLayer.left;
      const anchorRight = canvasLayer.right - tagAnchorRect.right;
      const anchorTop = tagAnchorRect.top - canvasLayer.top;
      const anchorBottom = canvasLayer.bottom - tagAnchorRect.bottom;

      // determine the bounds
      const anchorXMin = Math.round((anchorLeft - leftDistance) / this.scale);
      const anchorXMax =
        canvasWidth - Math.round((anchorRight - rightDistance) / this.scale);
      const anchorYMin = Math.round((anchorTop - topDistance) / this.scale);
      const anchorYMax =
        canvasHeight - Math.round((anchorBottom - bottomDistance) / this.scale);

      return {
        xMin: anchorXMin,
        xMax: anchorXMax,
        yMin: anchorYMin,
        yMax: anchorYMax,
        x: tagAnchorRect.left,
        y: tagAnchorRect.top,
      };
    },

    getScaledAmount() {
      // default size based on 3040x1960 - scale = 1
      if (this.$refs.canvasWrapper) {
        let canvasWidth =
          this.$refs.canvasWrapper.getBoundingClientRect().width;
        let scaled = canvasWidth / 3190;
        this.scale = scaled;
      }
    },

    clearSelection() {
      this.statusDot = null;

      this.isTagDragging = false;
      this.isTagDraggable = false;
    },

    handleDragStart(tag, e) {
      e.target.setPointerCapture(e.pointerId);

      this.isTagDraggable = true;
      this.statusDot = null;

      this.$store.commit("draggable/addToSelectedElements", {
        element: tag,
        elementType: "tag",
        isSingle: !e.shiftKey,
      });

      this.$emit("record-before-action", tag);
    },

    handleDrag(event) {
      if (this.isTagDraggable) {
        this.isTagDragging = true;

        const tagEl = document.getElementById(this.activeElement._id);
        const tagBounds = this.getTagBoundingBox(
          tagEl,
          this.activeElement.type
        );

        const canvasEl = document.querySelector(".draggable-container__bg");
        const canvasRect = canvasEl.getBoundingClientRect();

        if (!this.clickOffsetX) {
          this.clickOffsetX = tagBounds.x - event.pageX;
        }
        if (!this.clickOffsetY) {
          this.clickOffsetY = tagBounds.y - event.pageY;
        }

        const layerX =
          (event.clientX - canvasRect.left + this.clickOffsetX) / this.scale;
        const layerY =
          (event.clientY - canvasRect.top + this.clickOffsetY) / this.scale;

        const cx = Math.min(Math.max(layerX, tagBounds.xMin), tagBounds.xMax);
        const cy = Math.min(Math.max(layerY, tagBounds.yMin), tagBounds.yMax);

        this.activeElement.pos.x = cx;
        this.activeElement.pos.y = cy;
      }
    },

    handleDragEnd(e) {
      e.target.releasePointerCapture(e.pointerId);

      this.clickOffsetX = null;
      this.clickOffsetY = null;

      this.isTagDragging = false;
      this.isTagDraggable = false;

      this.$emit("record-action", this.activeElement);
    },

    onDoubleClick(tag, event) {
      if (event && event.shiftKey) {
        this.$emit("select-all-of-type", tag);
        return;
      }
      // don't open panel if multiple selected
      if (this.selectedElements && this.selectedElements.length > 1) {
        return;
      }
      this.$store.commit("draggable/setIsMenuOpen", true);
    },

    openAssetLibrary(context, assetSelected) {
      this.$emit("openAssetLibrary", context, assetSelected);
    },

    isActive(tagId) {
      const tag = this.selectedElements.find(
        (element) => element._id === tagId
      );
      return !_.isEmpty(tag);
    },

    isInactive(tagId) {
      if (!this.selectedElements.length) return false;

      const tag = this.selectedElements.find(
        (element) => element._id === tagId
      );
      return _.isEmpty(tag);
    },

    selectSlide(id) {
      const slide = this.page.interactiveSlides.find(
        (slide) => slide._id === id
      );
      this.$store.commit("draggable/setActiveSlide", slide);
    },

    openMenu() {
      this.$store.commit("draggable/setIsMenuOpen", true);
    },
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.getScaledAmount);
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";
@import "@/styles/_mixins.scss";

.tags-wrapper {
  bottom: 0;
  left: 0;
  overflow: hidden;
  position: absolute;
  right: 0;
  top: 0;
}

.tag-layer {
  height: 100%;
  pointer-events: none;
  position: absolute;
  width: 100%;
  z-index: 10;

  &--inactive {
    opacity: 0.5;
  }
}

.interactive-slides {
  align-items: flex-end;
  bottom: 5em;
  display: flex;
  justify-content: center;
  pointer-events: none;
  position: absolute;
  width: 100%;
  z-index: 20;

  &__slide {
    background: transparent;
    border-radius: 0.5em;
    box-shadow: $tag-box-shadow;
    display: inline-block;
    color: #ffffff;
    cursor: pointer;
    height: 8.25em;
    margin: 0 1em;
    overflow: hidden;
    pointer-events: auto;
    position: relative;
    transition: opacity 200ms ease;
    user-select: none;
    width: 12.5em;

    &--add-btn {
      appearance: none;
      background: transparent;
      box-shadow: none;
      border: 0.2em dashed rgba(255, 255, 255, 0.8);
      color: #ffffff;
    }

    &--no-bg {
      background-position: 0px 0px, 10px 10px;
      background-size: 20px 20px;
      background-image: linear-gradient(
          45deg,
          #eee 25%,
          transparent 25%,
          transparent 75%,
          #eee 75%,
          #eee 100%
        ),
        linear-gradient(
          45deg,
          #eee 25%,
          white 25%,
          white 75%,
          #eee 75%,
          #eee 100%
        );
    }

    &--inactive {
      opacity: 0.5;
    }

    img {
      height: 100%;
      object-fit: cover;
      pointer-events: none;
      width: 100%;
    }
  }

  &__label {
    align-items: center;
    background: rgba(0, 0, 0, 0.6);
    bottom: 0;
    display: flex;
    font-size: 0.6em;
    font-weight: 300;
    justify-content: center;
    height: 2.1em;
    letter-spacing: 0.05em;
    padding: 0.2em 0.75em;
    position: absolute;
    text-transform: uppercase;
    width: 100%;
  }
}

.slide-tag-layer {
  height: 100%;
  pointer-events: none;
  position: absolute;
  width: 100%;
  z-index: 15;
}
</style>
